<script setup lang="ts">
/**
 * Presentational list. Receives rows + pagination state + filter-count
 * + treasury-account picker state via props; emits row-click /
 * update:page / filter-click / update:treasuryAccountId. No queries,
 * no mutations, no router pushes — orchestration lives in the
 * container.
 *
 * The treasury-account quick filter renders inside the table's
 * `#quick-filters` slot (alongside the filter funnel button), not in a
 * standalone row above the table. The container owns the treasury
 * accounts query and forwards the list + loading state via props.
 *
 * Search input is omitted (the filter drawer carries name / email
 * filters). Sort order is owned by the filter drawer too — the table
 * does not render its own sort dropdown.
 */
import { computed } from 'vue'
import Box from '@uiKit/elements/Box.vue'
import Text from '@uiKit/elements/Text.vue'
import Select from '@uiKit/elements/Select.vue'
import StatusBadge from '@uiKit/components/StatusBadge.vue'
import DataTableV2, { type TableColumn } from '@uiKit/components/DataTableV2.vue'
import FiatTransactionTypeBadge from './FiatTransactionTypeBadge.vue'
import { formatMoney } from '@shared/utils'
import { formatDateTime } from '@shared/utils/dateTimeUtils'
import {
  referenceTypeColor,
  referenceTypeLabel,
  transactionReasonLabel,
  transactionStatusColor,
  transactionStatusLabel,
} from '../utils/fiatTransactionLabels'
import type { FiatTransactionRow } from '../types/fiatTransactionsTypes'
import type { TreasuryBankAccount } from '../../treasuryAccounts'

const props = defineProps<{
  rows: FiatTransactionRow[]
  loading: boolean
  total: number
  page: number
  pageSize: number
  filterCount: number
  emptyTitle?: string
  emptySubtitle?: string
  treasuryAccountId: string | null
  treasuryAccounts: TreasuryBankAccount[]
  treasuryAccountsLoading: boolean
  title?: string
  subtitle?: string
}>()

const emit = defineEmits<{
  (e: 'update:page', value: number): void
  (e: 'update:pageSize', value: number): void
  (e: 'row-click', row: FiatTransactionRow): void
  (e: 'filter-click'): void
  (e: 'update:treasuryAccountId', value: string | null): void
}>()

interface TreasuryAccountOption {
  label: string
  value: string | null
}

const treasuryAccountOptions = computed<TreasuryAccountOption[]>(() => {
  const base: TreasuryAccountOption[] = [{ label: 'All treasury accounts', value: null }]
  return [
    ...base,
    ...props.treasuryAccounts.map((account) => ({
      label: account.bankName
        ? `${account.accountName} — ${account.bankName}`
        : account.accountName,
      value: account.id,
    })),
  ]
})

const treasuryAccountModel = computed<string | null>({
  get: () => props.treasuryAccountId,
  set: (value) => emit('update:treasuryAccountId', value ?? null),
})

const columns: TableColumn[] = [
  { key: 'when', label: 'WHEN', width: 'minmax(170px, 1fr)' },
  { key: 'investor', label: 'INVESTOR', width: 'minmax(220px, 1.6fr)' },
  { key: 'type', label: 'TYPE', width: 'minmax(110px, 0.7fr)' },
  { key: 'reason', label: 'REASON', width: 'minmax(160px, 1fr)' },
  { key: 'amount', label: 'AMOUNT', width: 'minmax(160px, 1fr)', align: 'right' },
  { key: 'referenceType', label: 'REFERENCE TYPE', width: 'minmax(150px, 1fr)' },
  { key: 'status', label: 'STATUS', width: 'minmax(130px, 0.8fr)' },
]

function formatAmount(row: FiatTransactionRow): string {
  return formatMoney(row.currencyCode, row.amount)
}
</script>

<template>
  <DataTableV2
    :columns="columns"
    :rows="rows"
    :loading="loading"
    row-key="id"
    :title="title"
    :subtitle="subtitle"
    :searchable="false"
    :local-search="false"
    :local-pagination="false"
    :filterable="true"
    :filter-count="filterCount"
    :page="page"
    :page-size="pageSize"
    :total="total"
    :url-sync="true"
    page-size-storage-key="treasury.fiatTransactions.pageSize"
    clickable
    :empty-title="emptyTitle ?? 'No transactions found'"
    :empty-subtitle="emptySubtitle ?? 'Transactions matching your filters will appear here.'"
    @update:page="emit('update:page', $event)"
    @update:page-size="emit('update:pageSize', $event)"
    @filter-click="emit('filter-click')"
    @row-click="emit('row-click', $event as FiatTransactionRow)"
  >
    <template #quick-filters>
      <Select
        v-model="treasuryAccountModel"
        :options="treasuryAccountOptions"
        option-label="label"
        option-value="value"
        :placeholder="treasuryAccountsLoading ? 'Loading…' : 'All treasury accounts'"
        :disabled="treasuryAccountsLoading"
        size="small"
        class="fiat-transactions-list__account-select"
      />
    </template>

    <template #cell-when="{ row }">
      <Text type="body-sm" color="muted">{{
        formatDateTime((row as FiatTransactionRow).createdAt)
      }}</Text>
    </template>

    <template #cell-investor="{ row }">
      <Box flex direction="col">
        <Text type="body" weight="semibold">
          {{ (row as FiatTransactionRow).investorName || '—' }}
        </Text>
        <Text type="caption" color="muted">
          {{ (row as FiatTransactionRow).investorEmail || '—' }}
        </Text>
      </Box>
    </template>

    <template #cell-type="{ row }">
      <FiatTransactionTypeBadge :type="(row as FiatTransactionRow).transactionType" />
    </template>

    <template #cell-reason="{ row }">
      <Text type="body-sm">{{
        transactionReasonLabel((row as FiatTransactionRow).transactionReason)
      }}</Text>
    </template>

    <template #cell-amount="{ row }">
      <Text
        type="body-sm"
        weight="semibold"
        align="right"
        style="font-variant-numeric: tabular-nums"
      >
        {{ formatAmount(row as FiatTransactionRow) }}
      </Text>
    </template>

    <template #cell-referenceType="{ row }">
      <StatusBadge
        :color="referenceTypeColor((row as FiatTransactionRow).referenceType)"
        :label="referenceTypeLabel((row as FiatTransactionRow).referenceType)"
      />
    </template>

    <template #cell-status="{ row }">
      <StatusBadge
        :color="transactionStatusColor((row as FiatTransactionRow).status)"
        :label="transactionStatusLabel((row as FiatTransactionRow).status)"
      />
    </template>
  </DataTableV2>
</template>

<style scoped>
.fiat-transactions-list__account-select {
  min-width: 240px;
  max-width: 320px;
}
</style>
